import axios from 'axios';
import subDays from 'date-fns/subDays';
import { formatDate } from 'helpers/date';
import type { SubscriptionCancelReasonType } from 'helpers/enums';
import * as Rx from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { API_MYEDUZZ } from 'settings';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import type { IProductSales } from 'v2/interfaces/product';
import { ECustomerSearchTypes, EDateTypes } from 'v2/interfaces/subscriptions';
import type {
  CreateSubscriptionPlanPayload,
  ICancelReason,
  ISubscriptionInvoice,
  ISubscriptions,
  ISubscriptionsFilter,
  ISubscriptionsStatus,
  ResolveSubscriptionChangeResponse,
  StoreCommunicationMessageLogPayload,
  SubscriptionPlan,
  UpdateSubscriptionPlanPayload,
  ValidateWhatsAppMessageResponse
} from 'v2/interfaces/subscriptions';

import apiService from './api';
import type { ApiService } from './api';
import tableService from './table';
import type { TableService } from './table';

const initialFilter: ISubscriptionsFilter = {
  date_type: EDateTypes.CREATED,
  date: [formatDate(subDays(new Date(), 7), 'dd/MM/yyyy'), formatDate(new Date(), 'dd/MM/yyyy')],
  customer_type: ECustomerSearchTypes.EMAIL,
  payment_method: [],
  customer: '',
  id: [],
  status: [],
  product_id: [],
  invoice_id: [],
  affiliate_id: [],
  negotiated_invoices: false,
  unlimited_installment: false,
  open_invoices: false,
  type: [],
  advanced: false
};

class SubscriptionsService {
  private data$ = new Rx.BehaviorSubject<ISubscriptions[]>([]);
  private filter$ = new Rx.BehaviorSubject<ISubscriptionsFilter>(initialFilter);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  private activeSubscription$ = new Rx.BehaviorSubject<number>(0);
  private showAdvancedResult$ = new Rx.BehaviorSubject<boolean>(false);
  private resetForm$ = new Rx.BehaviorSubject<boolean>(false);
  private filterIsAdvanced$ = new Rx.BehaviorSubject<boolean>(false);
  private products$ = new Rx.BehaviorSubject<Omit<IProductSales, 'children'>[]>([]);
  private status$ = new Rx.BehaviorSubject<ISubscriptionsStatus[]>([]);
  private loadingProducts$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingStatus$ = new Rx.BehaviorSubject<boolean>(false);
  private token: string;

  constructor(
    private apiService: ApiService,
    private tableService: TableService
  ) {}

  public setToken(token: string) {
    this.token = token;
  }

  public getToken(): string {
    return this.token;
  }

  public isLoadingProducts = () => this.loadingProducts$;
  public isLoadingStatus = () => this.loadingStatus$;

  public getFilterIsAdvanced = (): Rx.Observable<boolean> => this.filterIsAdvanced$;

  public getShowAdvancedResult = (): Rx.Observable<boolean> => this.showAdvancedResult$;

  public setFilterIsAdvanced = (state: boolean): void => this.filterIsAdvanced$.next(state);

  public setActiveSubscription = (id: number) => this.activeSubscription$.next(id);

  public getActiveSubscription = () => this.activeSubscription$;

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public resetPage() {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });

    return this.getPaginationParams();
  }

  public getProducts = (): Rx.Observable<Omit<IProductSales, 'children'>[]> => this.products$;
  public getStatus = () => this.status$;

  public setProducts = (products: any[]) => {
    this.products$.next(products);
  };

  public loadStatus() {
    this.apiService
      .get('subscriptions/status')
      .pipe(
        tap(() => this.loadingStatus$.next(true)),
        map(response => response.data),
        tap(() => this.loadingStatus$.next(false))
      )
      .subscribe((data: any) => this.status$.next(data));
  }

  public loadDeliveries() {
    this.apiService
      .get('subscriptions/products')
      .pipe(
        tap(() => this.loadingProducts$.next(true)),
        map(response => response.data),
        tap(() => this.loadingProducts$.next(false))
      )
      .subscribe((products: any) => this.products$.next(products));
  }

  public setPagination(pagination: IPaginationParams) {
    this.paginationParams$.next(pagination);
  }

  public getSubscriptions = (): Rx.Observable<ISubscriptions[]> => this.data$;

  public getFilter = (): Rx.Observable<ISubscriptionsFilter> => this.filter$;

  public getResetForm = () => this.resetForm$;
  public setResetForm = (value: boolean) => this.resetForm$.next(value);

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public cleanFilter() {
    return this.filter$.next(initialFilter);
  }

  public setFilter(filter: ISubscriptionsFilter) {
    this.filter$.pipe(take(1)).subscribe(currentFilter => {
      this.showAdvancedResult$.next(filter.advanced);
      this.filter$.next({ ...currentFilter, ...filter });
    });
  }

  public load() {
    return this.getFilter().pipe(
      map(f => this.correctFilter(f)),
      switchMap(filter => {
        return this.resetPage().pipe(map(params => ({ ...filter, ...params })));
      }),
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap(filter => this.apiService.get('/subscriptions', filter)),
      tap(response => this.data$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination)),
      tap(response => this.totalItems$.next(response?.pagination?.total_items)),
      tap(() => this.activeSubscription$.next(0)),
      tap(() => this.tableService.changeLoading(false)),
      catchError(() => {
        this.tableService.changeLoading(false);
        this.data$.next([]);
        this.activeSubscription$.next(0);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public export() {
    return this.apiService.get('/subscriptions', this.correctFilter({ ...this.filter$.value, export: 1 }));
  }

  public getSubscriptionCancelReasons(type?: SubscriptionCancelReasonType): Rx.Observable<ICancelReason[]> {
    return this.apiService
      .get('/subscriptions/cancel-reasons', type ? { type } : {})
      .pipe(map(response => response.data));
  }

  public cancelSubscription(contractId: number, motive: { reason_id: number; reason_description?: string }) {
    return this.apiService.post(`/subscriptions/${contractId}/cancel`, motive);
  }

  public reactivateSubscription(contractId: number) {
    return this.apiService.post(`/subscriptions/${contractId}/reactivate`, {});
  }

  public getSubscription(contractId: number): Rx.Observable<ISubscriptions> {
    return this.apiService.get(`/subscriptions/${contractId}`).pipe(map(response => response.data));
  }

  public suspendSubscription(contractId: number, motive: { reason_id: number; reason_description?: string }) {
    return this.apiService.post(`/subscriptions/${contractId}/suspend`, motive);
  }

  public cancelSubscriptionAgend(
    contractId: number,
    motive: { reason_id: number; reason_description?: string },
    dateCancel: string
  ) {
    return this.apiService.post(`/subscriptions/${contractId}/cancel`, { ...motive, date_cancel: dateCancel });
  }

  public sendNegotiateInvoices(contractId: number, invoices: number[]) {
    return this.apiService.post(`/subscriptions/${contractId}/invoices/negociate`, { invoices });
  }

  public changeChargeEmail(contractId: number, chargeEmail: string): Rx.Observable<string> {
    return this.apiService.post(`/subscriptions/${contractId}/charge_email`, { charge_email: chargeEmail }).pipe(
      tap(() =>
        this.data$.next(
          [...this.data$.value].map(item => {
            if (item.id === contractId) {
              return { ...item, charge_email: chargeEmail };
            }

            return item;
          })
        )
      )
    );
  }

  public changePaymentBankslip(contractId: number): Rx.Observable<string> {
    return this.apiService.post(`/subscriptions/${contractId}/bankslip`, {}).pipe(
      tap(() =>
        this.data$.next(
          [...this.data$.value].map(item => {
            if (item.id === contractId) {
              return { ...item, payment_method: { id: 1, name: 'Boleto Bancário' } };
            }

            return item;
          })
        )
      )
    );
  }

  public setNewDueDate(contractId: number, invoiceId: number, newDueDate: string) {
    return this.apiService.post(`/subscriptions/${contractId}/duedate/${invoiceId}`, { due_date: newDueDate });
  }

  public addAffiliate(contractId: number, email: string) {
    return this.apiService.post(`/subscriptions/${contractId}/addAffiliate`, { affiliate_email: email }).pipe(
      tap(() =>
        this.data$.next(
          [...this.data$.value].map(item => {
            if (item.id === contractId) {
              return { ...item, affiliate: { email, id: 999999 } };
            }

            return item;
          })
        )
      )
    );
  }

  public getInvoicesByContractId(id: number): Rx.Observable<ISubscriptionInvoice[]> {
    if (id === 0) {
      return Rx.of([]);
    }

    return this.apiService.get(`/subscriptions/${id}/invoices`, {}).pipe(map(response => response.data));
  }

  public getRemoveAccessDate(
    id: number,
    skipContentRemoveCheck?: boolean,
    getSuggestedDate?: boolean
  ): Rx.Observable<string> {
    if (!id) {
      return Rx.of(null);
    }

    const params: Record<string, boolean> = {};

    if (skipContentRemoveCheck !== undefined && skipContentRemoveCheck !== null) {
      params.skipContentRemoveCheck = skipContentRemoveCheck;
    }

    return this.apiService.get(`/subscriptions/${id}/removeAccessDate`, params).pipe(
      map(response => {
        if (getSuggestedDate && response.data.suggestedDate) {
          return response.data.suggestedDate;
        }

        return response.data.removeAccessDate;
      })
    );
  }

  public async subscriptionCancelMFA(id: number) {
    return axios({
      baseURL: API_MYEDUZZ,
      url: `/subscriptions/${id}/cancelMFA`,
      method: 'POST',
      data: {},
      headers: {
        Authorization: this.getToken()
      }
    });
  }

  public async subscriptionSuspendMFA(id: number) {
    return axios({
      baseURL: API_MYEDUZZ,
      url: `/subscriptions/${id}/suspendMFA`,
      method: 'POST',
      data: {},
      headers: {
        Authorization: this.getToken()
      }
    });
  }

  public createSubscriptionPlan(productId: number, plan: CreateSubscriptionPlanPayload): Promise<void> {
    return this.apiService.post(`/products/${productId}/plans`, plan).toPromise();
  }

  public updateSubscriptionPlan(productId: number, plan: UpdateSubscriptionPlanPayload): Promise<void> {
    return this.apiService.put(`/products/${productId}/plan`, plan).toPromise();
  }

  public getSubscriptionPlan(subscriptionPlanId: number): Promise<SubscriptionPlan> {
    return this.apiService
      .get(`/products/plan/${subscriptionPlanId}`)
      .pipe(map(res => res.data))
      .toPromise();
  }

  public resolveRecurringDateChange(token: string, action: string): Rx.Observable<ResolveSubscriptionChangeResponse> {
    return this.apiService.post('/subscriptions/recurring-date/resolve-change', { token, action }, {}, false, true);
  }

  public storeCommunicationMessageLog(payload: StoreCommunicationMessageLogPayload): Promise<void> {
    return this.apiService.post('/subscriptions/communication/logs', payload, {}, false, true).toPromise();
  }

  public validateWhatsAppMessage(sentMessageId: string): Promise<ValidateWhatsAppMessageResponse> {
    return this.apiService
      .post('/subscriptions/communication/validate-whatsapp-message', { sentMessageId }, {}, false, true)
      .pipe(map(response => response.data))
      .toPromise();
  }

  public blacklistMessage(sentMessageId: string): Promise<void> {
    return this.apiService
      .post('/subscriptions/communication/blacklist', { sentMessageId }, {}, false, true)
      .toPromise();
  }

  private correctFilter(filter: ISubscriptionsFilter) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { date, customer, customer_type, date_type, advanced, ...restFilter } = filter;

    const getFilter = () => {
      const newFilter: any = {};

      if (customer) {
        newFilter[customer_type] = customer;
      }

      return newFilter;
    };

    const dateInitial = `${date_type}_initial`;
    const dateFinal = `${date_type}_final`;

    return {
      [dateInitial]: date[0],
      [dateFinal]: date[1],
      ...getFilter(),
      ...restFilter
    };
  }
}

const subscriptionsService = new SubscriptionsService(apiService, tableService);
export default subscriptionsService;
